import { useState } from "react";
import { Terminal, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/site";

const STATUS_COLORS = {
  RECEIVED: "#34C759",
  DIAGNOSING: "#FFCC00",
  "WAITING ON PARTS": "#FFCC00",
  "IN REPAIR": "#0055FF",
  "READY FOR PICKUP": "#34C759",
  "SHIPPED BACK": "#34C759",
  COMPLETED: "#34C759",
  CANCELLED: "#FF3B30",
};

export default function StatusTracker() {
  const [ticket, setTicket] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async () => {
    if (!ticket.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const res = await api.get(`/bookings/${ticket.trim().toUpperCase()}`);
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Ticket not found");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="track" className="relative py-24 sm:py-32 border-t border-white/5 bg-[#0A0A0A]" data-testid="status-tracker-section">
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <div className="mb-12">
          <div className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-4">
            [ 06 ] · LIVE STATUS TRACKER
          </div>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4">
            Where's my <span className="text-[#34C759]">repair?</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl">
            Enter the ticket number from your confirmation email to see exactly what's happening with your device.
          </p>
        </div>

        {/* Terminal widget */}
        <div className="relative bg-black border border-zinc-800 rounded-md overflow-hidden scanline">
          <div className="flex items-center gap-2 px-4 py-3 bg-[#121212] border-b border-zinc-800">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FF3B30]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#FFCC00]" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#34C759]" />
            </div>
            <div className="font-mono text-xs text-zinc-500 ml-2 flex items-center gap-2">
              <Terminal className="w-3 h-3" /> tombs@repair-lab : ~ / status
            </div>
          </div>

          <div className="p-6 sm:p-10 font-mono text-sm">
            <div className="text-zinc-500 mb-2">$ tracker --query</div>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Input
                value={ticket}
                onChange={(e) => setTicket(e.target.value.toUpperCase())}
                placeholder="TR-XXXXXX"
                className="bg-[#0A0A0A] border-zinc-800 text-[#34C759] font-mono uppercase placeholder:text-zinc-700 focus:border-[#34C759] focus:ring-[#34C759]"
                data-testid="status-tracker-input"
                onKeyDown={(e) => e.key === "Enter" && lookup()}
              />
              <Button
                onClick={lookup}
                disabled={loading}
                className="bg-[#34C759] text-black hover:bg-[#2ea64a] font-mono font-semibold rounded-md"
                data-testid="status-tracker-lookup-btn"
              >
                <Search className="w-4 h-4 mr-1" /> {loading ? "Querying..." : "Look up"}
              </Button>
            </div>

            {!result && !error && (
              <div className="text-zinc-600 cursor-blink" data-testid="status-tracker-idle">
                Awaiting input...
              </div>
            )}

            {error && (
              <div className="text-[#FF3B30]" data-testid="status-tracker-error">
                ERR &gt; {error}
              </div>
            )}

            {result && (
              <div className="space-y-2" data-testid="status-tracker-result">
                <div>
                  <span className="text-zinc-600">ticket_id     :</span>{" "}
                  <span className="text-[#FFCC00]">{result.ticket_id}</span>
                </div>
                <div>
                  <span className="text-zinc-600">device        :</span>{" "}
                  <span className="text-white">{result.device_type}</span>
                </div>
                <div>
                  <span className="text-zinc-600">service       :</span>{" "}
                  <span className="text-white">{result.service_type}</span>
                </div>
                <div>
                  <span className="text-zinc-600">status        :</span>{" "}
                  <span
                    className="font-bold terminal-text"
                    style={{
                      color: STATUS_COLORS[result.status] || "#34C759",
                      textShadow: `0 0 10px ${STATUS_COLORS[result.status] || "#34C759"}66`,
                    }}
                  >
                    {result.status}
                  </span>
                </div>
                {result.notes && (
                  <div>
                    <span className="text-zinc-600">notes         :</span>{" "}
                    <span className="text-zinc-300">{result.notes}</span>
                  </div>
                )}
                <div>
                  <span className="text-zinc-600">last_update   :</span>{" "}
                  <span className="text-zinc-400">{new Date(result.updated_at).toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
